import { useEffect, useState } from 'react'

import { getMvMRallyJoinEligibility } from '../common/api'

/**
 * Should the "Join attack" button on a rally mail be shown? (Mirror of useMvmReinforceAvailable.)
 *
 * Rule: the button exists only while the host is marching on the target or the battle is being fought.
 * Once the battle is over (the march is returning, completed, or cancelled) the button is gone — a mail is a
 * permanent record, the siege is not, and a button that dead-ends on "the battle already ended" is worse
 * than no button. The server's verdict (`/mvm/rally/eligibility`, the same check the join enforces)
 * decides; this hook only mirrors it.
 *
 * Freshness: a verdict is cached per wallet+attack for a short time so a list of mails costs one request
 * each. A mounted button re-asks on its own every {@link REVALIDATE_MS}, and immediately when something
 * invalidates the attack (`invalidateMvmRallyJoinAvailability`, fired on battle end), so it disappears
 * the moment the siege ends even while the mailbox stays open. Unknown (loading, or the check failed)
 * shows nothing.
 */
const VERDICT_TTL_MS = 30_000
const REVALIDATE_MS = 15_000

type CacheEntry = { available: boolean; at: number }

const verdictCache = new Map<string, CacheEntry>()
const inFlight = new Map<string, Promise<boolean>>()
/** Mounted buttons, keyed by wallet|attack, told to re-ask when their attack is invalidated. */
const subscribers = new Map<string, Set<() => void>>()

function cacheKey(wallet: string, attackId: string): string {
  return `${wallet.trim().toLowerCase()}|${attackId}`
}

function notify(attackId?: string): void {
  const suffix = attackId ? `|${attackId}` : null
  for (const [key, set] of subscribers) {
    if (suffix && !key.endsWith(suffix)) continue
    for (const fn of set) fn()
  }
}

/**
 * Drop cached verdicts so mounted buttons re-ask the server right away and the next render starts fresh.
 * Called when the wallet's standing in a siege changes (joined or left the rally) and when the
 * battle ends.
 */
export function invalidateMvmRallyJoinAvailability(attackId?: string): void {
  if (!attackId) {
    verdictCache.clear()
    inFlight.clear()
    notify()
    return
  }
  const suffix = `|${attackId}`
  for (const key of [...verdictCache.keys()]) {
    if (key.endsWith(suffix)) verdictCache.delete(key)
  }
  for (const key of [...inFlight.keys()]) {
    if (key.endsWith(suffix)) inFlight.delete(key)
  }
  notify(attackId)
}

async function fetchAvailability(wallet: string, attackId: string, force = false): Promise<boolean> {
  const key = cacheKey(wallet, attackId)
  const fresh = verdictCache.get(key)
  if (!force && fresh && Date.now() - fresh.at < VERDICT_TTL_MS) return fresh.available
  const pending = inFlight.get(key)
  if (pending) return pending
  const request = getMvMRallyJoinEligibility(wallet, attackId)
    .then((verdict) => {
      const available = verdict?.joinable === true
      verdictCache.set(key, { available, at: Date.now() })
      return available
    })
    .catch(() => {
      /** An unreachable check offers nothing — never cached, so the next ask retries. */
      return false
    })
    .finally(() => {
      inFlight.delete(key)
    })
  inFlight.set(key, request)
  return request
}

export function useMvmRallyJoinAvailable(
  walletAddress: string | null | undefined,
  attackId: string | null | undefined,
): boolean {
  const wallet = (walletAddress || '').trim()
  const id = (attackId || '').trim()
  const cached = wallet && id ? verdictCache.get(cacheKey(wallet, id)) : undefined
  const [available, setAvailable] = useState<boolean>(
    cached && Date.now() - cached.at < VERDICT_TTL_MS ? cached.available : false,
  )

  useEffect(() => {
    if (!wallet || !id) {
      setAvailable(false)
      return undefined
    }
    let active = true
    const ask = (force: boolean) => {
      void fetchAvailability(wallet, id, force).then((value) => {
        if (active) setAvailable(value)
      })
    }
    ask(false)
    const key = cacheKey(wallet, id)
    const onInvalidate = () => ask(true)
    const set = subscribers.get(key) ?? new Set<() => void>()
    set.add(onInvalidate)
    subscribers.set(key, set)
    const timer = window.setInterval(() => {
      if (document.hidden) return
      ask(true)
    }, REVALIDATE_MS)
    return () => {
      active = false
      window.clearInterval(timer)
      set.delete(onInvalidate)
      if (set.size === 0) subscribers.delete(key)
    }
  }, [wallet, id])

  return available
}
